import { useCallback, useEffect, useRef, useState } from 'react'
import { getGoogleWorkspaceOAuthUrl, disconnectGoogleWorkspace } from '../services/googleWorkspaceApi.js'
import { connectErrorCopy } from '../lib/connectErrorCopy'
import { markConnectPending, clearConnectPending, takeAbandonedConnect } from '../lib/pendingConnect'
import { toast } from '@/components/ui/use-toast'

export type UseGoogleWorkspaceIntegrationReturn = {
  connecting: boolean
  disconnecting: boolean
  showExplainer: boolean
  openConnectModal: () => void
  closeConnectModal: () => void
  confirmConnect: () => void
  disconnect: () => Promise<void>
}

// Outcomes surface as toasts (top-right, 4s auto-dismiss) rather than the
// inline FlashBanner the other provider cards use, so a connect result is
// visible wherever the rep is looking on the page. Toast copy is written for
// the rep; anything diagnostic goes to the console here and to the API logs on
// the server side.
export function useGoogleWorkspaceIntegration(workspaceId: string, onRefresh?: () => void): UseGoogleWorkspaceIntegrationReturn {
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [showExplainer, setShowExplainer] = useState(false)
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  // A rep who backs out of Google's consent screen returns with no outcome in
  // the URL. Without this they'd land on a card that still says "Connect" and
  // no explanation — or, if the browser restored the page from bfcache, on a
  // spinner stuck mid-"Continue to Google" forever.
  const reportAbandonedConnect = useCallback(() => {
    if (!takeAbandonedConnect()) return
    setConnecting(false)
    setShowExplainer(false)
    toast({
      variant: 'warning',
      title: 'Google Workspace not connected',
      description: 'You came back before finishing. You can connect whenever you\'re ready.',
    })
  }, [])

  // Read ?googleWorkspace= on return from OAuth, then clean the URL. A failure
  // carries ?reason — a typed code, mapped to rep-facing copy; the matching
  // diagnostic lives in the API logs.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const outcome = params.get('googleWorkspace')

    if (outcome !== 'connected' && outcome !== 'error') {
      reportAbandonedConnect()
      return
    }

    clearConnectPending()

    if (outcome === 'connected') {
      toast({ variant: 'success', title: 'Google Workspace connected', description: 'Zeph can now use your Gmail and calendar.' })
      onRefreshRef.current?.()
    } else {
      const reason = params.get('reason')
      console.error('[google-workspace] connect failed', { reason })
      toast({
        variant: 'destructive',
        title: "Couldn't connect Google Workspace",
        description: connectErrorCopy(reason),
      })
    }

    params.delete('googleWorkspace')
    params.delete('reason')
    const newSearch = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`)
  }, [reportAbandonedConnect])

  // A back-navigation from Google can restore this page from bfcache, which
  // replays no effects and would otherwise leave `connecting` stuck true.
  useEffect(() => {
    function onPageShow(event: PageTransitionEvent) {
      if (event.persisted) reportAbandonedConnect()
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [reportAbandonedConnect])

  const openConnectModal = useCallback(() => setShowExplainer(true), [])
  const closeConnectModal = useCallback(() => setShowExplainer(false), [])

  const confirmConnect = useCallback(async () => {
    setConnecting(true)
    try {
      const url = await getGoogleWorkspaceOAuthUrl(workspaceId)
      markConnectPending()
      window.location.href = url
    } catch (err) {
      setConnecting(false)
      setShowExplainer(false)
      clearConnectPending()
      console.error('[google-workspace] failed to start OAuth', err)
      toast({
        variant: 'destructive',
        title: "Couldn't connect Google Workspace",
        description: 'Zeph couldn\'t reach Google just now. Please try again.',
      })
    }
  }, [workspaceId])

  const disconnect = useCallback(async () => {
    setDisconnecting(true)
    try {
      await disconnectGoogleWorkspace(workspaceId)
      toast({ variant: 'success', title: 'Google Workspace disconnected', description: 'Zeph no longer has access to your Gmail or calendar.' })
    } catch (err) {
      console.error('[google-workspace] disconnect failed', err)
      toast({
        variant: 'destructive',
        title: "Couldn't disconnect Google Workspace",
        description: 'Your account is still connected. Please try again.',
      })
    } finally {
      setDisconnecting(false)
    }
  }, [workspaceId])

  return {
    connecting,
    disconnecting,
    showExplainer,
    openConnectModal,
    closeConnectModal,
    confirmConnect,
    disconnect,
  }
}
